/**
 * Unit review composer.
 */
import React, { Box, Grid, TextField } from '@mui/material';
import { useFormik } from "formik";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useAsterController } from 'context';
import { actionEditReview } from 'context/action';
import { VText, VFormItem, VButton, VImage } from "form";
import { getSectionTitle, getSequenceTitle, getVerticalIdFromIndex, getVerticalTitle } from 'utils/course';

const REVIEW_TYPE = {
  BAD: 'bad',
  NOT_CORRECT: 'incorrect',
  LEARNED: 'enough',
  LIKE: 'good'
};

/**
 * Lets the learner save a like/dislike review for the current vertical.
 */
const BoardReview = () => {

  const { t } = useTranslation('common');

  const { courseId, sectionIndex, sequenceIndex, verticalIndex } = useParams();

  const [controller, dispatch] = useAsterController();
  const { structure, review } = controller;

  const onSubmit = (values) => {
    const verticalId = getVerticalIdFromIndex(structure, sectionIndex, sequenceIndex, verticalIndex);

    const value = {
      course_id: courseId,
      vertical_id: verticalId,
      section_title: getSectionTitle(structure, sectionIndex),
      sequence_title: getSequenceTitle(structure, sectionIndex, sequenceIndex),
      vertical_title: getVerticalTitle(structure, sectionIndex, sequenceIndex, verticalIndex),
      type: values.type,
      content: values.content
    };
    actionEditReview(dispatch, value);

  };

  const formik = useFormik({
    initialValues: {
      content: '',
      type: ''
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      content: Yup.string().max(200, 'Must be 15 characters or less').required('This field is required'),
    }),
    onSubmit: onSubmit
  });

  return <>
    <Box className="w-full mt-20">
      {
        !review.type ?
          <form onSubmit={formik.handleSubmit}>
            <Grid container className=" mx-auto max-w-4xl">
              <Box className="w-full flex justify-center mb-10">
                <VText className="text-2xl text-center">{t("How did you find this content?")}</VText>
              </Box>
              <Grid item xs={12} className="mb-5">
                <Box className="flex flex-row items-start flex-wrap justify-center gap-2 sm:gap-10 lg:gap-20" >
                  <Box className="flex flex-col justify-center md:w-auto w-16 items-center gap-5" >
                    <VButton variant="contained" color={formik.values.type === REVIEW_TYPE.BAD ? "primary" : "light"} className="flex justify-center items-center px-5 py-2" onClick={() => formik.setFieldValue("type", REVIEW_TYPE.BAD)} disableElevation>
                      <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji1.svg`} />
                    </VButton>
                    <VText color="secondary">{t("I didn't get it")}</VText>
                  </Box>
                  <Box className="flex flex-col justify-center md:w-auto w-16 items-center gap-5" >
                    <VButton variant="contained" color={formik.values.type === REVIEW_TYPE.NOT_CORRECT ? "primary" : "light"} className="flex justify-center items-center px-5 py-2" onClick={() => formik.setFieldValue("type", REVIEW_TYPE.NOT_CORRECT)} disableElevation>
                      <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji2.svg`} />
                    </VButton>
                    <VText color="secondary">{t("It was not accurate")}</VText>
                  </Box>
                  <Box className="flex flex-col justify-center md:w-auto w-16 items-center gap-5" >
                    <VButton variant="contained" color={formik.values.type === REVIEW_TYPE.LEARNED ? "primary" : "light"} className="flex justify-center items-center px-5 py-2" onClick={() => formik.setFieldValue("type", REVIEW_TYPE.LEARNED)} disableElevation>
                      <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji3.svg`} />
                    </VButton>
                    <VText color="secondary">{t("I learn something")}</VText>
                  </Box>
                  <Box className="flex flex-col justify-center md:w-auto w-16 items-center gap-5" >
                    <VButton variant="contained" color={formik.values.type === REVIEW_TYPE.LIKE ? "primary" : "light"} className="flex justify-center items-center px-5 py-2" onClick={() => formik.setFieldValue("type", REVIEW_TYPE.LIKE)} disableElevation>
                      <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji4.svg`} />
                    </VButton>
                    <VText color="secondary">{t("I Like")}</VText>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid container>
              {
                formik.values.type && <Grid item xs={12}>
                  <VFormItem formik={formik} name="content" label={t("Your comment")} placeholder={t("Type content...")} required>
                    <TextField
                      name="content"
                      size="small"
                      value={formik.values.content}
                      onChange={formik.handleChange}
                      rows={5}
                      multiline
                      className="w-full"
                      color="secondary"
                    />
                  </VFormItem>
                  <Box className="flex justify-center">
                    <VButton variant="contained" color="primary" className="w-40 mt-5" type="submit" disabled={!formik.values.content}>
                      {t("Submit")}
                    </VButton>
                  </Box>
                </Grid>
              }
            </Grid>
          </form>
          :
          <Box>
            <VText className="font-bold text-xl" div>{t("Thanks for your review!")}</VText>

            <Box className="flex items-start gap-2 mt-4">
              {review.type === REVIEW_TYPE.BAD && <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji1.svg`} />}
              {review.type === REVIEW_TYPE.NOT_CORRECT && <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji2.svg`} />}
              {review.type === REVIEW_TYPE.LEARNED && <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji3.svg`} />}
              {review.type === REVIEW_TYPE.LIKE && <VImage src={`${process.env.REACT_APP_S3_ASSET_ENDPOINT}/v2-assets/app/Emoji4.svg`} />}

              <VText className="italic" div>{review.content}</VText>
            </Box>
          </Box>
      }

    </Box>
  </>;
};

export default BoardReview;